import {
  ACTIVITY_ACTIONS,
  ACTIVITY_APPLICATION_IDS,
  ACTIVITY_TOOL_IDS,
  TRIBUNALEA_APPLICATIONS,
  TRIBUNALEA_TOOLS,
} from "@tribunalea/shared";
import {
  Activity,
  AppWindow,
  BarChart3,
  Coins,
  CreditCard,
  LoaderCircle,
  MousePointerClick,
  RefreshCw,
  Search,
  UsersRound,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const number = new Intl.NumberFormat("es-AR");
const credits = new Intl.NumberFormat("es-AR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
const dateTime = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "America/Argentina/Mendoza",
});
const shortDate = new Intl.DateTimeFormat("es-AR", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});

const actionLabels = {
  [ACTIVITY_ACTIONS.PORTAL_OPENED]: "Portal abierto",
  [ACTIVITY_ACTIONS.APP_OPENED]: "Aplicación abierta",
  [ACTIVITY_ACTIONS.TOOL_OPENED]: "Herramienta abierta",
  [ACTIVITY_ACTIONS.TOOL_USED]: "Herramienta utilizada",
  [ACTIVITY_ACTIONS.CREDIT_PURCHASED]: "Crédito comprado",
  [ACTIVITY_ACTIONS.CREDIT_USED]: "Crédito utilizado",
};

const applicationOptions = [
  ...TRIBUNALEA_APPLICATIONS.map(({ id, name }) => ({ id, label: name })),
  { id: ACTIVITY_APPLICATION_IDS.ACCOUNT, label: "Cuenta" },
  { id: ACTIVITY_APPLICATION_IDS.ASTREA, label: "Astrea" },
];

const toolOptions = [
  ...TRIBUNALEA_TOOLS.map(({ id, name, applicationId }) => ({
    id,
    label: name,
    appId: applicationId,
  })),
  { id: ACTIVITY_TOOL_IDS.ACCOUNT_CREDITS, label: "Créditos", appId: "account" },
  { id: ACTIVITY_TOOL_IDS.ACCOUNT_SIGNATURE, label: "Firma", appId: "account" },
  { id: ACTIVITY_TOOL_IDS.ACCOUNT_ORGANIZATION, label: "Organización", appId: "account" },
  { id: ACTIVITY_TOOL_IDS.ASTREA_LIBRARY, label: "Biblioteca", appId: "astrea" },
  { id: ACTIVITY_TOOL_IDS.ASTREA_ASSISTANT, label: "Asistente", appId: "astrea" },
];

const appLabels = Object.fromEntries(
  applicationOptions.map(({ id, label }) => [id, label]),
);
const toolLabels = Object.fromEntries(
  toolOptions.map(({ id, label }) => [id, label]),
);

const initialFilters = {
  days: 30,
  workspaceId: "",
  action: "",
  appId: "",
  toolId: "",
};

function readableIdentifier(value) {
  if (!value) return "—";
  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function actionCount(metrics, action) {
  return metrics?.actions?.find(({ id }) => id === action)?.count || 0;
}

function dailySeries(metrics) {
  if (!metrics?.period?.from || !metrics?.period?.to) return metrics?.daily || [];
  const values = new Map((metrics.daily || []).map((item) => [item.date, item]));
  const result = [];
  const cursor = new Date(metrics.period.from);
  const end = new Date(metrics.period.to);
  while (cursor <= end && result.length < 367) {
    const key = cursor.toLocaleDateString("en-CA", {
      timeZone: metrics.period.timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    if (!result.some(({ date }) => date === key)) {
      result.push(values.get(key) || {
        date: key,
        events: 0,
        activeUsers: 0,
        toolUses: 0,
      });
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
}

function Breakdown({ title, description, items, labelFor }) {
  const maximum = Math.max(1, ...items.map(({ count }) => count));
  return (
    <section className="activity-panel activity-breakdown-panel">
      <header>
        <div>
          <span>Distribución</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </header>
      {items.length ? (
        <div className="activity-breakdown">
          {items.slice(0, 8).map((item) => (
            <article key={item.id}>
              <div>
                <span>{labelFor(item)}</span>
                <strong>{number.format(item.count)}</strong>
              </div>
              <span className="activity-progress">
                <span style={{ width: `${Math.max(3, (item.count / maximum) * 100)}%` }} />
              </span>
              <small>{number.format(item.activeUsers || 0)} usuario(s) activo(s)</small>
            </article>
          ))}
        </div>
      ) : <p className="activity-empty-inline">Sin actividad para estos filtros.</p>}
    </section>
  );
}

function TimelineRow({ event }) {
  return (
    <article className="activity-event-row">
      <span className={`activity-event-dot activity-event-dot--${event.action}`} />
      <div className="activity-event-main">
        <strong>{actionLabels[event.action] || readableIdentifier(event.action)}</strong>
        <span>
          {appLabels[event.appId] || readableIdentifier(event.appId)}
          {event.toolId ? ` · ${toolLabels[event.toolId] || readableIdentifier(event.toolId)}` : ""}
        </span>
        {event.step ? <small>{readableIdentifier(event.step)}</small> : null}
      </div>
      <div className="activity-event-identity">
        <strong>{event.userId || "Usuario desconocido"}</strong>
        <span>{event.workspaceId || "Sin workspace"}</span>
        {event.actorUserId ? <small>Actor: {event.actorUserId}</small> : null}
      </div>
      <div className="activity-event-value">
        {Number.isFinite(event.payload?.credits) ? (
          <strong>{credits.format(event.payload.credits)} créditos</strong>
        ) : <strong>{event.source === "browser" ? "Navegación" : event.source === "billing" ? "Créditos" : "API"}</strong>}
        <span>{event.occurredAt ? dateTime.format(new Date(event.occurredAt)) : "—"}</span>
      </div>
    </article>
  );
}

export function UserActivityAdminPage({ api }) {
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [metrics, setMetrics] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");

  const requestFilters = useMemo(() => ({
    ...filters,
    ...(selectedUser ? { userId: selectedUser.id } : {}),
    page,
    limit: 25,
  }), [filters, page, selectedUser]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    try {
      const [nextMetrics, nextTimeline] = await Promise.all([
        api.userActivityMetrics(filters),
        selectedUser ? api.userActivity(requestFilters) : Promise.resolve(null),
      ]);
      setMetrics(nextMetrics);
      setTimeline(nextTimeline);
      setError("");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, filters, requestFilters, selectedUser]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (selectedUser) return undefined;
    const query = userQuery.trim();
    if (query.length < 2) {
      setUserResults([]);
      setUserSearchLoading(false);
      setUserSearchError("");
      return undefined;
    }
    let active = true;
    const timeout = window.setTimeout(async () => {
      setUserSearchLoading(true);
      setUserSearchError("");
      try {
        const results = await api.activityUsers(query);
        if (active) setUserResults(results);
      } catch (searchError) {
        if (active) {
          setUserResults([]);
          setUserSearchError(searchError.message);
        }
      } finally {
        if (active) setUserSearchLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [api, selectedUser, userQuery]);

  const days = useMemo(() => dailySeries(metrics), [metrics]);
  const maxDailyEvents = Math.max(1, ...days.map(({ events }) => events));
  const metricsCards = [
    {
      label: "Usuarios activos",
      value: metrics?.totals?.activeUsers || 0,
      detail: "Usuarios únicos del período",
      icon: UsersRound,
    },
    {
      label: "Eventos",
      value: metrics?.totals?.events || 0,
      detail: "Aperturas, usos y créditos",
      icon: Activity,
    },
    {
      label: "Apps abiertas",
      value: actionCount(metrics, ACTIVITY_ACTIONS.APP_OPENED),
      detail: "Sesiones de producto",
      icon: AppWindow,
    },
    {
      label: "Herramientas usadas",
      value: actionCount(metrics, ACTIVITY_ACTIONS.TOOL_USED),
      detail: "Interacciones completadas",
      icon: MousePointerClick,
    },
    {
      label: "Créditos comprados",
      value: metrics?.totals?.creditsPurchased || 0,
      detail: "Pagos y suscripciones",
      icon: CreditCard,
      format: credits,
    },
    {
      label: "Créditos utilizados",
      value: metrics?.totals?.creditsUsed || 0,
      detail: "Descuentos efectivos",
      icon: Coins,
      format: credits,
    },
  ];
  const filteredTools = toolOptions.filter(({ appId }) => !draft.appId || appId === draft.appId);

  function updateDraft(key, value) {
    setDraft((current) => ({
      ...current,
      [key]: value,
      ...(key === "appId" && current.toolId
        && toolOptions.find(({ id }) => id === current.toolId)?.appId !== value
        ? { toolId: "" }
        : {}),
    }));
  }

  function applyFilters(event) {
    event.preventDefault();
    setPage(1);
    setFilters({
      ...draft,
      workspaceId: draft.workspaceId.trim(),
    });
  }

  function selectAction(action) {
    const nextAction = filters.action === action ? "" : action;
    const next = { ...draft, action: nextAction };
    setDraft(next);
    setFilters(next);
    setPage(1);
  }

  function selectUser(user) {
    setSelectedUser(user);
    setTimeline(null);
    setUserQuery("");
    setUserResults([]);
    setUserSearchError("");
    setPage(1);
  }

  function clearSelectedUser() {
    setSelectedUser(null);
    setTimeline(null);
    setPage(1);
  }

  return (
    <div className="support-access-page activity-admin-page">
      <div className="support-access-heading activity-heading">
        <div>
          <span className="support-access-eyebrow">ANALÍTICA DE PRODUCTO</span>
          <h1>Actividad de usuarios</h1>
          <p>Aperturas, uso real de herramientas y movimientos de créditos en una única vista.</p>
        </div>
        <button className="jobs-refresh" onClick={() => load({ quiet: true })} disabled={loading || refreshing}>
          <RefreshCw size={15} className={refreshing ? "doctrine-spin" : ""} />
          Actualizar
        </button>
      </div>

      <form className="activity-filters" onSubmit={applyFilters}>
        <label>
          Período
          <select value={draft.days} onChange={(event) => updateDraft("days", Number(event.target.value))}>
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
            <option value={365}>Último año</option>
          </select>
        </label>
        <label>
          Workspace
          <input value={draft.workspaceId} onChange={(event) => updateDraft("workspaceId", event.target.value)} placeholder="personal:… u org_…" />
        </label>
        <label>
          Acción
          <select value={draft.action} onChange={(event) => updateDraft("action", event.target.value)}>
            <option value="">Todas</option>
            {Object.entries(actionLabels).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
        <label>
          Aplicación
          <select value={draft.appId} onChange={(event) => updateDraft("appId", event.target.value)}>
            <option value="">Todas</option>
            {applicationOptions.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
        <label>
          Herramienta
          <select value={draft.toolId} onChange={(event) => updateDraft("toolId", event.target.value)}>
            <option value="">Todas</option>
            {filteredTools.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}
          </select>
        </label>
        <button type="submit"><Search size={15} /> Aplicar filtros</button>
      </form>

      {error ? <div className="support-access-error">{error}</div> : null}

      <section className="activity-metrics" aria-label="Resumen de actividad">
        {metricsCards.map(({ label, value, detail, icon: Icon, format = number }) => (
          <article key={label}>
            <span><Icon size={19} /></span>
            <div>
              <small>{label}</small>
              <strong>{loading && !metrics ? "—" : format.format(value)}</strong>
              <p>{detail}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="activity-panel activity-daily-panel">
        <header>
          <div>
            <span>Evolución</span>
            <h2>Actividad diaria</h2>
            <p>Eventos totales y usuarios únicos por día, en horario de Mendoza.</p>
          </div>
          <BarChart3 size={21} />
        </header>
        {days.some(({ events }) => events) ? (
          <div className="activity-chart-scroll">
            <div className="activity-chart" style={{ minWidth: `${Math.max(640, days.length * 25)}px` }}>
              {days.map((day) => (
                <div className="activity-chart-day" key={day.date} title={`${day.date}: ${day.events} eventos · ${day.activeUsers} usuarios`}>
                  <span className="activity-chart-value">{day.events || ""}</span>
                  <span className="activity-chart-track">
                    <span style={{ height: `${day.events ? Math.max(5, (day.events / maxDailyEvents) * 100) : 2}%` }} />
                  </span>
                  <small>{shortDate.format(new Date(`${day.date}T12:00:00.000Z`))}</small>
                </div>
              ))}
            </div>
          </div>
        ) : <p className="activity-empty-inline">No hay eventos en el período seleccionado.</p>}
      </section>

      <div className="activity-columns">
        <Breakdown
          title="Aplicaciones"
          description="Participación de cada producto en la actividad registrada."
          items={metrics?.applications || []}
          labelFor={(item) => appLabels[item.id] || readableIdentifier(item.id)}
        />
        <Breakdown
          title="Herramientas"
          description="Las funciones con mayor cantidad de aperturas, usos o consumos."
          items={metrics?.tools || []}
          labelFor={(item) => toolLabels[item.id] || readableIdentifier(item.id)}
        />
      </div>

      <section className="activity-panel activity-actions-panel">
        <header>
          <div>
            <span>Comportamiento</span>
            <h2>Eventos por acción</h2>
            <p>Seleccioná una acción para aplicarla como filtro al tablero.</p>
          </div>
        </header>
        <div className="activity-action-grid">
          {Object.entries(actionLabels).map(([id, label]) => (
            <button key={id} type="button" className={filters.action === id ? "is-selected" : ""} onClick={() => selectAction(id)}>
              <span>{label}</span>
              <strong>{number.format(actionCount(metrics, id))}</strong>
            </button>
          ))}
        </div>
      </section>

      <section className="activity-panel activity-timeline-panel">
        <header>
          <div>
            <span>Detalle</span>
            <h2>Línea de tiempo</h2>
            <p>{selectedUser
              ? `${number.format(timeline?.pagination?.total || 0)} eventos de ${selectedUser.fullName}.`
              : "Seleccioná un usuario para consultar sus eventos."}</p>
          </div>
          {timeline?.pagination?.truncated ? <small>Vista limitada a los 5.000 eventos más recientes</small> : null}
        </header>
        <div className="activity-user-picker">
          {selectedUser ? (
            <div className="activity-selected-user">
              {selectedUser.imageUrl ? (
                <img src={selectedUser.imageUrl} alt="" />
              ) : (
                <span aria-hidden="true">{selectedUser.fullName.slice(0, 1).toUpperCase()}</span>
              )}
              <div>
                <strong>{selectedUser.fullName}</strong>
                <small>{selectedUser.email || selectedUser.id}</small>
                {selectedUser.email ? <small>{selectedUser.id}</small> : null}
              </div>
              <button type="button" onClick={clearSelectedUser}><X size={15} /> Cambiar usuario</button>
            </div>
          ) : (
            <div className="activity-user-search">
              <label>
                <Search size={17} aria-hidden="true" />
                <input
                  type="search"
                  value={userQuery}
                  onChange={(event) => setUserQuery(event.target.value)}
                  placeholder="Buscar por nombre, email o ID de Clerk"
                  autoComplete="off"
                />
                {userSearchLoading ? <LoaderCircle size={17} className="doctrine-spin" /> : null}
              </label>
              {userSearchError ? <div className="activity-user-search-message is-error">{userSearchError}</div> : null}
              {!userSearchError && userQuery.trim().length < 2 ? (
                <div className="activity-user-search-message">Escribí al menos dos caracteres para buscar.</div>
              ) : null}
              {!userSearchLoading && userQuery.trim().length >= 2 && !userSearchError ? (
                <div className="activity-user-results">
                  {userResults.length ? userResults.map((user) => (
                    <button type="button" key={user.id} onClick={() => selectUser(user)}>
                      {user.imageUrl ? (
                        <img src={user.imageUrl} alt="" />
                      ) : (
                        <span aria-hidden="true">{user.fullName.slice(0, 1).toUpperCase()}</span>
                      )}
                      <span>
                        <strong>{user.fullName}</strong>
                        <small>{user.email || user.id}</small>
                      </span>
                      <small>{user.id}</small>
                    </button>
                  )) : <div className="activity-user-search-message">No encontramos usuarios.</div>}
                </div>
              ) : null}
            </div>
          )}
        </div>
        {!selectedUser ? (
          <div className="activity-timeline-placeholder">
            <UsersRound size={24} />
            <strong>Elegí un usuario</strong>
            <span>La línea de tiempo se carga únicamente para la persona seleccionada.</span>
          </div>
        ) : loading && !timeline ? (
          <div className="support-access-state"><LoaderCircle className="doctrine-spin" /> Cargando actividad…</div>
        ) : timeline?.items?.length ? (
          <div className="activity-event-list">
            {timeline.items.map((event) => <TimelineRow key={event.id} event={event} />)}
          </div>
        ) : <p className="activity-empty-inline">No hay eventos para estos filtros.</p>}
        {selectedUser && (timeline?.pagination?.pages || 1) > 1 ? (
          <footer className="activity-pagination">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>Anterior</button>
            <span>Página {page} de {timeline.pagination.pages}</span>
            <button type="button" disabled={page >= timeline.pagination.pages || loading} onClick={() => setPage((current) => current + 1)}>Siguiente</button>
          </footer>
        ) : null}
      </section>
    </div>
  );
}
